import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Settings, 
  Upload, 
  AlertCircle, 
  CheckCircle, 
  Loader2, 
  Trash2, 
  Download, 
  X, 
  FileImage,
  BarChart3
} from 'lucide-react';
import { exportToCSV, formatArrayField, generateBatchId, formatTimestamp } from '../utils/csvExport';

/**
 * VisitingCardOCRPage Component
 * 
 * Intelligent visiting card OCR feature with:
 * - Multi-image upload with preview
 * - Session memory accumulation (multiple processing cycles)
 * - CSV export functionality
 * - Batch tracking and metadata display
 * - Modern, mobile-friendly UI following KISS principles
 */
const VisitingCardOCRPage = () => {
  // Configuration state
  const [config, setConfig] = useState({
    baseUrl: 'https://dea29b8d5617.ngrok-free.app',
    ocrEndpoint: '/ocr/fast'
  });

  // Upload state
  const [uploadedImages, setUploadedImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  // Data state - session memory
  const [accumulatedCards, setAccumulatedCards] = useState([]);
  const [processingBatches, setProcessingBatches] = useState([]);

  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showConfig, setShowConfig] = useState(false);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  // Handle configuration changes
  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Validate file type and size
  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type.toLowerCase())) {
      return { valid: false, error: `Invalid file type: ${file.name}. Only JPEG and PNG are allowed.` };
    }
    if (file.size > maxSize) {
      return { valid: false, error: `File too large: ${file.name}. Maximum size is 10MB.` };
    }
    return { valid: true };
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const maxFiles = 20;

    if (files.length > maxFiles) {
      setError(`Too many files selected. Maximum is ${maxFiles} images per upload.`);
      return;
    }

    // Validate all files
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(validation.error);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    } else {
      setError(null);
    }

    if (validFiles.length > 0) {
      // Create preview URLs
      const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
      
      setUploadedImages(prev => [...prev, ...validFiles]);
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
      setSuccessMessage(null);
    }

    // Reset input
    event.target.value = '';
  };

  // Handle remove single image
  const handleRemoveImage = (index) => {
    // Revoke preview URL
    URL.revokeObjectURL(previewUrls[index]);

    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Handle clear all data
  const handleClearAll = () => {
    // Revoke all preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));

    setUploadedImages([]);
    setPreviewUrls([]);
    setAccumulatedCards([]);
    setProcessingBatches([]);
    setError(null);
    setSuccessMessage(null);
  };

  // Handle process cards
  const handleProcessCards = async () => {
    if (uploadedImages.length === 0) {
      setError('Please select at least one image to process');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Create FormData
      const formData = new FormData();
      uploadedImages.forEach(file => {
        formData.append('files', file);
      });

      // Make API request
      const apiUrl = `${config.baseUrl}${config.ocrEndpoint}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Validate response
      if (!data.success) {
        throw new Error(data.message || 'OCR processing failed');
      }

      // Generate batch ID and timestamp
      const batchId = generateBatchId();
      const timestamp = data.timestamp || new Date().toISOString();

      // Map meta.Refs to cards
      const cardsWithMetadata = data.structured_cards.map(card => {
        const cardMeta = data.meta?.find(m => m.cardid === card.cardid);
        return {
          ...card,
          sourceImages: cardMeta?.Refs || [],
          batchId,
          timestamp
        };
      });

      // Merge with accumulated cards
      setAccumulatedCards(prev => [...prev, ...cardsWithMetadata]);

      // Track batch
      setProcessingBatches(prev => [...prev, {
        batchId,
        timestamp,
        imageCount: data.total_images || uploadedImages.length,
        cardCount: data.structured_cards.length,
        successCount: data.successful_count || 0,
        failedCount: data.failed_count || 0
      }]);

      // Clear uploaded images
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      setUploadedImages([]);
      setPreviewUrls([]);

      // Show success message
      setSuccessMessage(
        `Successfully processed ${data.total_images || uploadedImages.length} images and extracted ${data.structured_cards.length} cards!`
      );

    } catch (err) {
      setError(err.message || 'An error occurred while processing the images');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle CSV export
  const handleExportCSV = () => {
    if (accumulatedCards.length === 0) {
      setError('No cards to export');
      return;
    }

    try {
      exportToCSV(accumulatedCards);
      setSuccessMessage(`Exported ${accumulatedCards.length} cards to CSV successfully!`);
    } catch (err) {
      setError('Failed to export CSV: ' + err.message);
    }
  };

  // Render configuration panel
  const renderConfigPanel = () => (
    <div className="mb-6">
      <button
        onClick={() => setShowConfig(!showConfig)}
        className="flex items-center space-x-2 text-white hover:text-purple-200 transition-colors"
      >
        <Settings className="w-5 h-5" />
        <span>Configuration</span>
      </button>

      {showConfig && (
        <div className="mt-4 bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                OCR API Base URL
              </label>
              <input
                type="text"
                value={config.baseUrl}
                onChange={(e) => handleConfigChange('baseUrl', e.target.value)}
                className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                placeholder="https://api.example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                OCR Endpoint Path
              </label>
              <input
                type="text"
                value={config.ocrEndpoint}
                onChange={(e) => handleConfigChange('ocrEndpoint', e.target.value)}
                className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                placeholder="/ocr/fast"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render upload section
  const renderUploadSection = () => (
    <div className="mb-6">
      <label className="block text-sm font-medium text-white mb-2">
        Upload Visiting Card Images
      </label>

      {/* File Input */}
      <div className="mb-4">
        <input
          type="file"
          id="file-upload"
          multiple
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileSelect}
          className="hidden"
        />
        <label
          htmlFor="file-upload"
          className="flex items-center justify-center space-x-2 w-full p-6 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-2 border-dashed border-purple-400/50 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-purple-600/30 transition-all duration-200"
        >
          <Upload className="w-6 h-6 text-purple-300" />
          <span className="text-purple-200 font-medium">
            Click to select images or drag and drop
          </span>
        </label>
        <p className="text-xs text-gray-400 mt-2">
          Supported formats: JPEG, PNG • Max size: 10MB per file • Max files: 20
        </p>
      </div>

      {/* Image Previews */}
      {uploadedImages.length > 0 && (
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-white">
              Selected Images ({uploadedImages.length})
            </h3>
            <button
              onClick={() => {
                previewUrls.forEach(url => URL.revokeObjectURL(url));
                setUploadedImages([]);
                setPreviewUrls([]);
              }}
              className="text-xs text-red-300 hover:text-red-200 transition-colors"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {uploadedImages.map((file, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square bg-black/20 rounded-lg overflow-hidden border border-white/10">
                  <img
                    src={previewUrls[index]}
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={() => handleRemoveImage(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
                <p className="text-xs text-gray-300 mt-1 truncate" title={file.name}>
                  {file.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 mt-4">
        <button
          onClick={handleProcessCards}
          disabled={isProcessing || uploadedImages.length === 0}
          className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2 min-h-[44px]"
        >
          {isProcessing ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <CreditCard className="w-5 h-5" />
          )}
          <span>{isProcessing ? 'Processing...' : 'Process Cards'}</span>
        </button>

        {accumulatedCards.length > 0 && (
          <button
            onClick={handleClearAll}
            className="w-full sm:w-auto px-6 py-3 bg-red-600/20 hover:bg-red-600/30 text-red-300 rounded-xl font-medium transition-colors duration-200 flex items-center justify-center space-x-2 min-h-[44px]"
          >
            <Trash2 className="w-4 h-4" />
            <span>Clear All Data</span>
          </button>
        )}
      </div>
    </div>
  );

  // Render summary statistics
  const renderSummaryStats = () => {
    if (accumulatedCards.length === 0) return null;

    const totalImages = processingBatches.reduce((sum, batch) => sum + batch.imageCount, 0);
    const totalSuccess = processingBatches.reduce((sum, batch) => sum + batch.successCount, 0);
    const totalFailed = processingBatches.reduce((sum, batch) => sum + batch.failedCount, 0);

    return (
      <div className="bg-gradient-to-r from-green-900/30 to-blue-900/30 backdrop-blur-sm rounded-xl border border-green-500/30 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white">Session Summary</h3>
          </div>
          <button
            onClick={handleExportCSV}
            className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500/50 transition-all duration-200 flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Download CSV</span>
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-sm text-gray-300 mb-1">Total Cards</p>
            <p className="text-2xl font-bold text-white">{accumulatedCards.length}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-sm text-gray-300 mb-1">Images Processed</p>
            <p className="text-2xl font-bold text-white">{totalImages}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-sm text-gray-300 mb-1">Successful</p>
            <p className="text-2xl font-bold text-green-400">{totalSuccess}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-sm text-gray-300 mb-1">Failed</p>
            <p className="text-2xl font-bold text-red-400">{totalFailed}</p>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-300">
          <p>Processing Batches: {processingBatches.length}</p>
        </div>
      </div>
    );
  };

  // Render single card item
  const renderCardItem = (card, index) => (
    <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 hover:border-white/20 transition-all duration-200">
      {/* Card Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{card.name || 'N/A'}</h3>
            <p className="text-xs text-gray-400">{card.cardid}</p>
          </div>
        </div>
      </div>

      {/* Card Details Grid */}
      <div className="space-y-3">
        {/* Role & Company */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
            <p className="text-xs text-blue-300 mb-1">Role</p>
            <p className="text-sm text-white">{card.role || 'N/A'}</p>
          </div>
          <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
            <p className="text-xs text-purple-300 mb-1">Company</p>
            <p className="text-sm text-white">{card.company || 'N/A'}</p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
          <p className="text-xs text-green-300 mb-2">Contact Information</p>
          <div className="space-y-2">
            <div>
              <p className="text-xs text-gray-400">Phone</p>
              <p className="text-sm text-white">{formatArrayField(card.phone)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Email</p>
              <p className="text-sm text-white break-all">{formatArrayField(card.email)}</p>
            </div>
          </div>
        </div>

        {/* Address */}
        {card.address && card.address !== 'N/A' && (
          <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3">
            <p className="text-xs text-orange-300 mb-1">Address</p>
            <p className="text-sm text-white">{card.address}</p>
          </div>
        )}

        {/* Website */}
        {card.website && card.website.length > 0 && (
          <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-3">
            <p className="text-xs text-cyan-300 mb-1">Website</p>
            <p className="text-sm text-white break-all">{formatArrayField(card.website)}</p>
          </div>
        )}

        {/* Metadata */}
        <div className="bg-gray-900/30 border border-gray-500/30 rounded-lg p-3">
          <p className="text-xs text-gray-400 mb-2">Metadata</p>
          <div className="space-y-1 text-xs text-gray-300">
            <p>Source: {formatArrayField(card.sourceImages)}</p>
            <p>Batch: {card.batchId}</p>
            <p>Time: {formatTimestamp(card.timestamp)}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Render cards grid
  const renderCardsGrid = () => {
    if (accumulatedCards.length === 0) return null;

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span>Extracted Cards ({accumulatedCards.length})</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accumulatedCards.map((card, index) => renderCardItem(card, index))}
        </div>
      </div>
    );
  };

  // Render empty state
  const renderEmptyState = () => {
    if (accumulatedCards.length > 0) return null;

    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-600/20 rounded-full mb-4">
          <FileImage className="w-10 h-10 text-purple-300" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">No Cards Scanned Yet</h3>
        <p className="text-gray-300 mb-6 max-w-md mx-auto">
          Upload visiting card images to extract contact information automatically.
          You can process multiple batches and export all data to CSV.
        </p>
        <div className="space-y-2 text-sm text-gray-400 max-w-md mx-auto">
          <p>✓ Upload multiple images at once</p>
          <p>✓ Process cards in multiple batches</p>
          <p>✓ Export all data to CSV</p>
          <p>✓ Session memory keeps all your scans</p>
        </div>
      </div>
    );
  };

  // Main render
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-500 rounded-2xl mb-4">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Visiting Card OCR Scanner
            </h1>
            <p className="text-xl text-purple-100">
              AI-powered business card data extraction with session memory
            </p>
          </div>

          {/* Configuration Panel */}
          {renderConfigPanel()}

          {/* Main Content */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            {/* Upload Section */}
            {renderUploadSection()}

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <span className="text-red-300 whitespace-pre-line">{error}</span>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="mb-6 p-4 bg-green-900/20 border border-green-500/30 rounded-lg flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-green-300">{successMessage}</span>
              </div>
            )}

            {/* Summary Statistics */}
            {renderSummaryStats()}

            {/* Cards Grid */}
            {renderCardsGrid()}

            {/* Empty State */}
            {renderEmptyState()}

            {/* Instructions */}
            <div className="mt-6 space-y-4">
              <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                <h3 className="text-sm font-medium text-purple-300 mb-2">How to use Visiting Card OCR:</h3>
                <ul className="text-sm text-purple-200 space-y-1">
                  <li>• Click "Click to select images" or drag and drop visiting card images</li>
                  <li>• Supported formats: JPEG, PNG (max 10MB per file, up to 20 files)</li>
                  <li>• Review the image previews and remove any unwanted images</li>
                  <li>• Click "Process Cards" to extract contact information</li>
                  <li>• All extracted cards are stored in session memory</li>
                  <li>• You can upload and process more cards - they will be added to your collection</li>
                  <li>• Click "Download CSV" to export all accumulated card data</li>
                  <li>• Use "Clear All Data" to reset and start fresh</li>
                </ul>
              </div>

              <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <h3 className="text-sm font-medium text-blue-300 mb-2">Features:</h3>
                <ul className="text-sm text-blue-200 space-y-1">
                  <li>• <strong>Session Memory</strong> - All cards from multiple processing cycles are kept in memory</li>
                  <li>• <strong>Batch Tracking</strong> - Each processing cycle is tracked with timestamp and metadata</li>
                  <li>• <strong>CSV Export</strong> - Export all accumulated data with proper formatting</li>
                  <li>• <strong>Multi-field Support</strong> - Handles multiple phone numbers, emails, and websites</li>
                  <li>• <strong>Source Tracking</strong> - See which image(s) each card was extracted from</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitingCardOCRPage;

